import {
  LinkedMedicalRecord,
  MedicalRecordAwareFileStore,
} from "#/api/fileStore.type";
import Card from "#/components/shared/card";
import Typography from "#/components/shared/typography";
import { formatTimeStampToHumanReadableDateTimeSeconds } from "#/components/utls/time";
import { Link } from "react-router-dom";

const medicalRecordSectionLabels: Record<string, string> = {
  diagnosis: "Diagnosis",
  medication: "Medication",
  provider: "Provider",
  test: "Test",
  vital: "Vital",
  visit: "Visit",
};

function buildMedicalRecordUrl(
  file: MedicalRecordAwareFileStore,
  entityType: string,
  entityId: string
) {
  const section = entityType?.toLowerCase();
  const sectionSlug = medicalRecordSectionLabels[section] ? section : null;
  const basePath = file.relation_id
    ? `/medical-records/user/${file.relation_id}`
    : `/medical-records`;
  if (!sectionSlug) {
    return basePath;
  }
  const pathWithSection = `${basePath}/section/${sectionSlug}`;
  const hash = `#record-${sectionSlug}-${entityId}`;
  return `${pathWithSection}${hash}`;
}

function formatDate(input?: string) {
  if (!input) return "";
  try {
    return formatTimeStampToHumanReadableDateTimeSeconds(input);
  } catch (error) {
    console.error("Failed to format timestamp", error);
    return input;
  }
}

export function FileStoreListItem({
  file,
}: {
  file: MedicalRecordAwareFileStore;
}) {
  const createdAt = formatDate(file.created_at ?? undefined);

  return (
    <Card variant="cream" elevation={1} className="space-y-4">
      <Card.Body className="space-y-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            <Typography variant="h4" className="text-terracotta">
              {file.name}
            </Typography>
            {createdAt && (
              <Typography variant="body2" className="text-brown-600">
                Uploaded {createdAt}
              </Typography>
            )}
            {file.notes && (
              <Typography variant="body2" className="text-brown-700">
                Notes: {file.notes}
              </Typography>
            )}
            {file.purpose && (
              <Typography variant="body2" className="text-brown-600">
                Purpose: {file.purpose}
              </Typography>
            )}
          </div>
          {file.signed_url && (
            <a
              href={file.signed_url}
              target="_blank"
              rel="noreferrer"
              className="self-start inline-flex items-center gap-2 rounded-lg border border-cream-500 bg-cream-500 px-3 py-1 text-sm font-medium text-brown-800 shadow-sm transition-colors hover:bg-cream-600"
            >
              Download
            </a>
          )}
        </div>

        {file.medicalRecords.length > 0 ? (
          <div className="space-y-2">
            <Typography variant="subtitle2" className="text-brown-700">
              Linked medical records
            </Typography>
            <ul className="space-y-2">
              {file.medicalRecords
                .filter(
                  (record: LinkedMedicalRecord) =>
                    record.entityType && record.entityId
                )
                .map((record: LinkedMedicalRecord) => {
                  const sectionLabel =
                    medicalRecordSectionLabels[
                      record.entityType?.toLowerCase()
                    ] ??
                    record.entityType ??
                    "Medical record";
                  const url = buildMedicalRecordUrl(
                    file,
                    record.entityType,
                    record.entityId
                  );
                  const linkedTime = record.linkedAt
                    ? formatDate(record.linkedAt)
                    : null;
                  const entitySummary = (() => {
                    const entity = record.entity;
                    if (!entity || typeof entity !== "object") return null;
                    const candidate =
                      (entity as any).name ||
                      (entity as any).description ||
                      (entity as any).title ||
                      (entity as any).notes;
                    return typeof candidate === "string" ? candidate : null;
                  })();
                  return (
                    <li
                      key={`${record.entityType}-${record.entityId}`}
                      className="flex flex-col"
                    >
                      <Link
                        to={url}
                        className="text-terracotta-600 hover:text-terracotta-700 underline"
                      >
                        {sectionLabel} · {record.entityId}
                      </Link>
                      <Typography variant="caption" className="text-brown-600">
                        {linkedTime
                          ? `Linked ${linkedTime}`
                          : "Linked medical record"}
                        {entitySummary ? ` · ${entitySummary}` : ""}
                      </Typography>
                    </li>
                  );
                })}
            </ul>
          </div>
        ) : (
          <Typography variant="body2" className="text-brown-600">
            No linked medical records yet.
          </Typography>
        )}
      </Card.Body>
    </Card>
  );
}

export default FileStoreListItem;
